/**
 * Passive capture of the element a tester picks, so pin source can be resolved
 * from the EXACT DOM node instead of re-querying by a (possibly non-unique)
 * selector at send time. pinpoint's addPin(selectedElement()) runs synchronously
 * from the "Add Pin" click; by then the last page element clicked in Select mode
 * IS the pinned element, so we snapshot it in store.save() keyed by pin id.
 *
 * pinpoint 0.1.10 gives us no hook to the picked element (its framework adapter
 * registry always resolves the built-in reactAdapter first, so a custom adapter
 * is never consulted), hence this listener-based approach rather than an adapter.
 */
export interface ElementCapture {
  /** Snapshot the currently-picked element under this pin id (call from save). */
  remember(pinId: string): void;
  /** The element remembered for a pin, or null. */
  elementFor(pinId: string): Element | null;
  /** Drop every association (call when the queue is cleared). */
  clearAll(): void;
  /** Remove the listener. */
  dispose: () => void;
}

import type { PinStorage } from "@agent-native/pinpoint";

const OVERLAY_ID = "pinpoint-root";

/**
 * Wrap a PinStorage so every saved pin snapshots the currently-picked element,
 * and a queue clear drops the associations. Delegates all other operations.
 */
export function withElementCapture(
  inner: PinStorage,
  capture: ElementCapture,
): PinStorage {
  return {
    load: (pageUrl) => inner.load(pageUrl),
    save: (pin) => {
      capture.remember(pin.id);
      return inner.save(pin);
    },
    update: (id, patch) => inner.update(id, patch),
    delete: (id) => inner.delete(id),
    list: (filter) => inner.list(filter),
    clear: (pageUrl) => {
      capture.clearAll();
      return inner.clear(pageUrl);
    },
  };
}

export function createElementCapture(doc: Document): ElementCapture {
  let picked: Element | null = null;
  const byPin = new Map<string, Element>();

  const onPick = (event: Event) => {
    const target = event.target;
    if (target instanceof Element && !target.closest(`#${OVERLAY_ID}`)) {
      picked = target;
    }
  };

  // Capture phase so we see the pick before pinpoint's own handlers, and both
  // event types since the picker may select on either.
  doc.addEventListener("pointerdown", onPick, true);
  doc.addEventListener("click", onPick, true);

  return {
    remember(pinId) {
      if (picked) byPin.set(pinId, picked);
    },
    elementFor(pinId) {
      return byPin.get(pinId) ?? null;
    },
    clearAll() {
      byPin.clear();
    },
    dispose() {
      doc.removeEventListener("pointerdown", onPick, true);
      doc.removeEventListener("click", onPick, true);
      byPin.clear();
    },
  };
}
